import { ShieldCheck, Lock, ServerCog, Eye, Fingerprint, ShieldAlert } from "lucide-react";
import { FadeUp } from "./AnimatedSection";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = [Lock, ServerCog, ShieldAlert, Fingerprint, Eye];

export const Security = () => {
  const { t } = useLanguage();
  const [featured, ...rest] = t.security.points;

  return (
    <section data-testid="security-section" className="relative py-28 md:py-36 bg-[#0C0C0C] border-y border-white/[0.06] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-brand-blue/[0.08] via-transparent to-transparent animate-float-slow" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-xl">
            <p className="text-brand-red text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-4">{t.security.label}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">{t.security.title}</h2>
          </div>
          <p className="text-neutral-400 text-sm md:text-base max-w-sm leading-relaxed">{t.security.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <FadeUp
            data-testid="security-point-0"
            className="lg:col-span-2 rounded-2xl border border-brand-blue/20 bg-gradient-to-b from-brand-blue/[0.08] to-transparent p-8 md:p-10 relative overflow-hidden"
          >
            <span className="absolute -right-6 -bottom-8 font-heading font-black text-brand-blue/[0.06] text-[10rem] leading-none select-none">01</span>
            <ShieldCheck className="w-9 h-9 mb-6 text-brand-blue relative z-10" strokeWidth={1.5} />
            <h3 className="font-heading font-bold text-white text-xl mb-3 relative z-10">{featured.title}</h3>
            <p className="text-neutral-400 text-sm leading-relaxed relative z-10 max-w-xs">{featured.desc}</p>
            <span className="inline-flex items-center gap-2 mt-8 text-[11px] font-mono uppercase tracking-widest text-brand-blue relative z-10">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse" /> {t.security.statusLabel}
            </span>
          </FadeUp>

          <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/[0.06] overflow-hidden">
            {rest.map((point, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <FadeUp
                  key={point.title}
                  delay={i * 0.06}
                  data-testid={`security-point-${i + 1}`}
                  className="flex items-start gap-4 p-5 md:p-6 hover:bg-white/[0.02] transition-colors duration-300"
                >
                  <Icon className="w-5 h-5 shrink-0 mt-0.5 text-brand-red" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading font-bold text-white text-[15px] mb-1">{point.title}</h4>
                    <p className="text-neutral-400 text-sm leading-relaxed">{point.desc}</p>
                  </div>
                  <span className="font-mono text-[10px] text-neutral-600 uppercase tracking-widest shrink-0 mt-1">{String(i + 2).padStart(2, "0")}</span>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
